/**
 * Locale-aware number formatting utilities
 * Supports German (comma) and English (dot) decimal separators
 */

/**
 * Get the decimal separator for a given locale
 * @param {string} locale - The locale code (e.g., 'de', 'en')
 * @returns {string} The decimal separator (',' for German, '.' for others)
 */
export function getDecimalSeparator(locale) {
  return locale === 'de' ? ',' : '.';
}

/**
 * Get the thousands separator for a given locale
 * @param {string} locale - The locale code (e.g., 'de', 'en')
 * @returns {string} The thousands separator ('.' for German, ',' for others)
 */
export function getThousandsSeparator(locale) {
  return locale === 'de' ? '.' : ',';
}

/**
 * Parse a localized number string to a JavaScript number
 * Accepts both comma and dot as decimal separators regardless of locale
 * 
 * @param {string|number} value - The value to parse
 * @param {string} locale - The locale code (optional, for future use)
 * @returns {number|null} The parsed number or null if invalid
 * 
 * @example
 * parseLocalizedNumber("10,5") // returns 10.5
 * parseLocalizedNumber("10.5") // returns 10.5
 * parseLocalizedNumber("1.234,56", "de") // returns 1234.56
 * parseLocalizedNumber("1,234.56", "en") // returns 1234.56
 */
export function parseLocalizedNumber(value, locale = 'en') {
  // Handle null, undefined, or empty values
  if (value == null || value === '') {
    return null;
  }

  // If already a number, return it
  if (typeof value === 'number') {
    return isNaN(value) ? null : value;
  }

  // Convert to string and trim whitespace
  let strValue = String(value).trim();

  if (strValue === '') {
    return null;
  }

  // Determine separators based on locale and input
  const decimalSep = getDecimalSeparator(locale);
  const thousandsSep = getThousandsSeparator(locale);

  // Count occurrences of comma and dot
  const commaCount = (strValue.match(/,/g) || []).length;
  const dotCount = (strValue.match(/\./g) || []).length;

  // Detect format and normalize:
  // If there are multiple separators, determine which is decimal vs thousands
  if (commaCount > 0 && dotCount > 0) {
    // Both separators present - determine which is decimal
    const lastCommaIdx = strValue.lastIndexOf(',');
    const lastDotIdx = strValue.lastIndexOf('.');
    
    if (lastCommaIdx > lastDotIdx) {
      // German format: 1.234.567,89 -> comma is decimal
      strValue = strValue.replace(/\./g, '').replace(',', '.');
    } else {
      // English format: 1,234,567.89 -> dot is decimal
      strValue = strValue.replace(/,/g, '');
    }
  } else if (commaCount > 1) {
    // Multiple commas - must be thousands separators (English format)
    strValue = strValue.replace(/,/g, '');
  } else if (dotCount > 1) {
    // Multiple dots - must be thousands separators (German format)
    strValue = strValue.replace(/\./g, '');
  } else if (commaCount === 1) {
    // Single comma - treat as decimal separator
    strValue = strValue.replace(',', '.');
  }
  // Single dot or no separators - already in standard format

  // Parse the normalized string
  const parsed = parseFloat(strValue);

  return isNaN(parsed) ? null : parsed;
}

/**
 * Format a number for display based on locale
 * 
 * @param {number|string} value - The value to format
 * @param {string} locale - The locale code (e.g., 'de', 'en')
 * @param {number} decimals - Maximum number of decimal places (default: 2)
 * @param {boolean} useGrouping - Whether to use thousands separator (default: false)
 * @returns {string} The formatted number string
 * 
 * @example
 * formatLocalizedNumber(10.5, 'de') // returns "10,5"
 * formatLocalizedNumber(10.5, 'en') // returns "10.5"
 * formatLocalizedNumber(1234.56, 'de', 2, true) // returns "1.234,56"
 */
export function formatLocalizedNumber(value, locale = 'en', decimals = 2, useGrouping = false) {
  // Handle null, undefined, or empty values
  if (value == null || value === '') {
    return '';
  }

  // Parse the value first to ensure it's a valid number
  const numValue = typeof value === 'number' ? value : parseLocalizedNumber(value, locale);

  if (numValue == null) {
    return '';
  }

  // Use Intl.NumberFormat for proper locale formatting
  const formatter = new Intl.NumberFormat(locale === 'de' ? 'de-DE' : 'en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: decimals,
    useGrouping: useGrouping
  });

  return formatter.format(numValue);
}

/**
 * Format a number for input fields (without thousands separator)
 * This is more suitable for text input fields where users type numbers
 * 
 * @param {number|string} value - The value to format
 * @param {string} locale - The locale code (e.g., 'de', 'en')
 * @param {number} decimals - Maximum number of decimal places (default: 10)
 * @returns {string} The formatted number string
 * 
 * @example
 * formatInputNumber(10.5, 'de') // returns "10,5"
 * formatInputNumber(10.5, 'en') // returns "10.5"
 */
export function formatInputNumber(value, locale = 'en', decimals = 10) {
  return formatLocalizedNumber(value, locale, decimals, false);
}

/**
 * Validate if a string is a valid number input (allows incomplete numbers during typing)
 * Accepts both comma and dot as decimal separators
 * 
 * @param {string} value - The value to validate
 * @returns {boolean} True if valid number input format
 * 
 * @example
 * isValidNumberInput("10") // true
 * isValidNumberInput("10.") // true (incomplete, but valid during typing)
 * isValidNumberInput("10,5") // true
 * isValidNumberInput("10.5") // true
 * isValidNumberInput("10,5,5") // false (multiple decimal separators)
 * isValidNumberInput("abc") // false
 */
export function isValidNumberInput(value) {
  if (value == null || value === '') {
    return true; // Allow empty input
  }

  const strValue = String(value);

  // Allow optional leading +/- sign, digits, and at most one comma or dot
  const validPattern = /^[-+]?[0-9]*[.,]?[0-9]*$/;
  
  if (!validPattern.test(strValue)) {
    return false;
  }

  // Ensure no more than one decimal separator (comma or dot)
  const commaCount = (strValue.match(/,/g) || []).length;
  const dotCount = (strValue.match(/\./g) || []).length;

  return (commaCount + dotCount) <= 1;
}

/**
 * Normalize a number input string by ensuring only one type of decimal separator
 * If both comma and dot are present, this might indicate an error
 * 
 * @param {string} value - The input value
 * @param {string} locale - The locale code
 * @returns {string} The normalized input string
 */
export function normalizeNumberInput(value, locale = 'en') {
  if (value == null || value === '') {
    return '';
  }

  const strValue = String(value);
  const decimalSep = getDecimalSeparator(locale);

  // Replace the "wrong" separator with the locale-appropriate one
  if (decimalSep === ',') {
    // For German locale, convert dots to commas (but only if it's a decimal separator)
    const dotCount = (strValue.match(/\./g) || []).length;
    if (dotCount === 1) {
      return strValue.replace('.', ',');
    }
  } else {
    // For English locale, convert commas to dots
    const commaCount = (strValue.match(/,/g) || []).length;
    if (commaCount === 1) {
      return strValue.replace(',', '.');
    }
  }

  return strValue;
}

export default {
  getDecimalSeparator,
  getThousandsSeparator,
  parseLocalizedNumber,
  formatLocalizedNumber,
  formatInputNumber,
  isValidNumberInput,
  normalizeNumberInput
};
